import {RootState} from "../../../../models"
import {LeadData, Signal} from "../../models"

/**
 * @param state
 */
export const getRecordsStateSlice = (state: RootState) => state.records;

/**
 * @param state
 */
export const selectFetching = (state: RootState) => getRecordsStateSlice(state).fetching;

/**
 * @param state
 */
export const selectCurrentRecord = (state: RootState) => getRecordsStateSlice(state).current;

/**
 * @param state
 */
export const selectCurrentApproving = (state: RootState) => getRecordsStateSlice(state).currentApproving;

/**
 * @param state
 */
export const selectRecordApproved = (state: RootState) => getRecordsStateSlice(state).recordApproved;

/**
 * @param state
 */
export const selectApproveDialogActive = (state: RootState) => getRecordsStateSlice(state).approveDialogActive;

/**
 * @param state
 */
//export const selectToggleGraph = (state: RootState) => getRecordsStateSlice(state).toggleGraph;

/**
 * @param state
 */
export const selectReceiveNext = (state: RootState) => getRecordsStateSlice(state).receiveNext;

/**
 * @param state
 */
export const selectReceiveNextError = (state: RootState) => getRecordsStateSlice(state).receiveNextError;

/**
 * @param state
 */
export const selectSignalFetching = (state: RootState) => getRecordsStateSlice(state).fetchingSignals;

/**
 * @param state
 */
export const selectCurrentRecordSignals = (state: RootState) => getRecordsStateSlice(state).currentSignals;

/**
 * @param state
 */
export const getSpeedAndAmplitudeStateSlice = (state: RootState) => state.speedAndAmplitude;

/**
 * @param state
 * @param lead
 */
export const selectLeadSignal = (state: RootState, lead: string) => {
    const signals = selectCurrentRecordSignals(state);

    if (signals === null) {
        return null
    }

    const signal = signals[lead];

    if (typeof signal === "undefined") {
        return null
    }

    return signal
};

/**
 * @param state
 */
export const selectCurrentRecordSignalsList = (state: RootState): Array<Signal> => {
    const signals = selectCurrentRecordSignals(state);

    if (signals === null) {
        return []
    }

    return Object.keys(signals).map(lead => signals[lead])
};

export const getMMPerSecond = (state: RootState): number => {
    const speedAndAmplitude = getSpeedAndAmplitudeStateSlice(state);

    if (speedAndAmplitude.mmPerSecond === undefined) {
        return 25
    }

    return speedAndAmplitude.mmPerSecond
};

export const getMMPerMv = (state: RootState): number => {
    const speedAndAmplitude = getSpeedAndAmplitudeStateSlice(state);

    if (speedAndAmplitude.mmPerMv === undefined) {
        return 10
    }

    return speedAndAmplitude.mmPerMv
};

/**
 * @param state
 */
export const selectCommonParameters = (state: RootState) => {
    const record = selectCurrentRecord(state);

    if (record === null) {
        return null
    }

    return record.commonParameters
};
/**
 * @param state
 */
export const selectParametersUpdating = (state: RootState) => getRecordsStateSlice(state).updatingParameters
/**
 * @param state
 */
export const selectParametersUpdated = (state: RootState) => getRecordsStateSlice(state).updatedParameters
/**
 * @param state
 */
export const selectParametersError = (state: RootState) => getRecordsStateSlice(state).parametersError
/**
 * @param state
 */
export const selectRecordConflict = (state: RootState) => getRecordsStateSlice(state).recordConflict
/**
 * @param state
 */
export const selectRecordMetaData = (state: RootState) => {
    const record = selectCurrentRecord(state);

    if (record === null) {
        return null
    }

    return record.metaData
};

/**
 * @param state
 */
export const selectRecordPatientInfo = (state: RootState) => {
    const record = selectCurrentRecord(state);

    if (record === null) {
        return null
    }

    return record.patientInfo
};

/**
 * @param state
 */
export const selectLeadsData = (state: RootState): Array<LeadData> => {
    const record = selectCurrentRecord(state);

    // `leadsData` is typed non-optional but the API omits it for records that
    // carry no per-lead signal (HOLTER, and ECGs fetched before the signal
    // endpoint resolves). Without this guard the reduce below throws
    // "Cannot read properties of undefined (reading 'reduce')" and takes the
    // whole page down — in BOTH skins, since withRecordData is composed at the
    // container level, above the Classic/Modern switch.
    if (record === null || !Array.isArray(record.leadsData)) {
        return []
    }

    const byLead = record.leadsData.reduce((byLead, leadData) => {
        return {
            ...byLead,
            [leadData.lead]: leadData,
        }
    }, {} as { [key: string]: LeadData });

    return [
        byLead["I"],
        byLead["II"],
        byLead["III"],
        byLead["AVR"],
        byLead["AVL"],
        byLead["AVF"],
        byLead["V1"],
        byLead["V2"],
        byLead["V3"],
        byLead["V4"],
        byLead["V5"],
        byLead["V6"],
    ]
};

export const selectConclusion = (state: RootState): string | null => {
    const record = selectCurrentRecord(state);

    if (record === null) {
        return null
    }

    return record.conclusion
};

export const selectUpdatingConclusion = (state: RootState) => getRecordsStateSlice(state).updatingConclusion;
export const selectConclusionEditing = (state: RootState) => getRecordsStateSlice(state).isConclusionEditing;
export const selectParameterEditing = (state: RootState) => getRecordsStateSlice(state).isParameterEditing;

export const selectInterpretation = (state: RootState): string | null => {
    const record = selectCurrentRecord(state);

    if (record === null) {
        return null
    }

    return record.interpretation
};
export const selectInterpretationUpdating = (state: RootState) => getRecordsStateSlice(state).isInterpretationUpdating;
export const selectInterpretationUpdated = (state: RootState) => getRecordsStateSlice(state).isInterpretationUpdated;

export const selectRecordPDF = (state: RootState) => getRecordsStateSlice(state).pdf;
export const selectRecordPDFfetching = (state: RootState) => getRecordsStateSlice(state).fetchingPDF;
